import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert, Button, Checkbox, DatePicker, Drawer, Dropdown, Form, Input, Modal, Select, Spin, TimePicker, Tooltip, message } from "antd";
import dayjs from "dayjs";
import { getWorkplaceNumber } from "@core/runtime-settings";
import {
  fetchSprNumberBranches,
  fetchSprNumberCategories,
  fetchSprNumberList
} from "../sprs/number/spr-number-api";
import { buildGroupTree, normalizeRow } from "../sprs/number/spr-number-utils";
import { docExpBook, docExpClearPlacement, docExpSellBookGroup, docExpSellBookPreview, fetchBronFreeMest, fetchBronGrid, fetchDocExpLookups } from "../doc/exp/doc-exp-api";
import { normalizeLookups, toApiDateTime } from "../doc/exp/doc-exp-utils";
import { getSetting, loadRuntimeSettings } from "@core/runtime-settings";
import DocExp from "../doc/exp/doc-exp";
import DocExpBookModal from "../doc/exp/doc-exp-book-modal";
import DocExpSellBookModal from "../doc/exp/doc-exp-sell-book-modal";
import { formatMoney, getLawFlag, STATUS, STATUS_LABELS } from "../doc/exp/doc-exp-utils";
import BronSearchFilter from "./bron-calendar-filters";
import refreshSrc from "../doc/plat/assets/refresh.svg";
import "./bron-calendar.css";

const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj?.[k] != null) return obj[k];
  }
  return undefined;
};

/**
 * Полный путь от выбранной группы (не включая её) до номера.
 * Без фильтра — от корня до номера.
 */
const pathFromSelectedToRoom = (roomName, roomIdUpper, selectedUpper, byId) => {
  const parts = [];
  let cur = Number(roomIdUpper) || 0;
  for (let i = 0; i < 64 && cur > 0; i += 1) {
    if (selectedUpper && cur === selectedUpper) break;
    const node = byId.get(cur);
    if (!node) break;
    const name = (node.name || "").trim();
    if (name) parts.push(name);
    cur = Number(node.idUpper) || 0;
    if (!selectedUpper && cur <= 0) break;
  }
  parts.reverse();
  const room = (roomName || "").trim();
  if (room) parts.push(room);
  return parts.join(" / ");
};

const STATE_CLASS = {
  0: "bron-cell--free",
  1: "bron-cell--partial",
  2: "bron-cell--busy",
  3: "bron-cell--empty"
};
/** Виды полосок = статусы путёвки. Легенда берётся из этого же списка. */
const BOOKING_KINDS = [
  { idStatus: STATUS.NEW, variant: "new", label: STATUS_LABELS[STATUS.NEW] },
  { idStatus: STATUS.CONFIRMED, variant: "confirmed", label: STATUS_LABELS[STATUS.CONFIRMED] },
  { idStatus: STATUS.CHECKED_IN, variant: "checked-in", label: STATUS_LABELS[STATUS.CHECKED_IN] },
  { idStatus: STATUS.CHECKED_OUT, variant: "checked-out", label: STATUS_LABELS[STATUS.CHECKED_OUT] },
  { idStatus: STATUS.CANCELLED, variant: "cancelled", label: STATUS_LABELS[STATUS.CANCELLED] }
];
const BOOKING_KIND_BY_STATUS = new Map(BOOKING_KINDS.map((k) => [k.idStatus, k]));
const FILTERS_STORAGE_KEY = "planOnline.bron.calendar.filters";
const BRANCH_STORAGE_KEY = "planOnline.bron.calendar.idBranch";
const isIsoDate = (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

const DAY_SECS = 24 * 60 * 60;
const MIN_BAR_DAYS = 1 / 24;

const toDayKey = (value) => {
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
};

/** Доля суток 0..1. Без времени (00:00) — середина клетки. */
const timeOfDayOffset = (value, fallback) => {
  if (value == null || value === "") return fallback;
  const d = dayjs(value);
  if (!d.isValid()) return fallback;
  const secs = d.hour() * 3600 + d.minute() * 60 + d.second();
  if (secs === 0) return fallback;
  return Math.min(1, secs / DAY_SECS);
};

const formatBookingWhen = (startAt, endAt) => {
  const fmt = (value) => {
    if (!value) return "";
    const d = dayjs(value);
    if (!d.isValid()) return "";
    const secs = d.hour() * 3600 + d.minute() * 60 + d.second();
    return secs === 0 ? d.format("DD.MM") : d.format("DD.MM HH:mm");
  };
  const a = fmt(startAt);
  const b = fmt(endAt);
  if (a && b) return `${a}–${b}`;
  return a || b || "";
};

const HintLines = ({ lines }) => {
  const items = (lines || []).filter(Boolean);
  if (!items.length) return null;
  return (
    <div className="bron-calendar__hint">
      {items.map((line, i) => (
        <div key={i}>{line}</div>
      ))}
    </div>
  );
};

const bookingAppearance = (booking) => {
  const idStatus = pick(booking, "idStatus", "IdStatus") ?? 0;
  const kind = BOOKING_KIND_BY_STATUS.get(idStatus);
  return {
    variant: kind?.variant || "checked-out",
    statusText: kind?.label || STATUS_LABELS[idStatus] || ""
  };
};

/** Непересекающиеся путёвки на одной дорожке, пересечения — столбиком. */
const layoutRoomBookings = (bookings, dateKeys) => {
  if (!dateKeys.length) return { items: [], lanes: 1 };
  const indexOf = new Map(dateKeys.map((k, i) => [k, i]));
  const last = dateKeys.length - 1;
  const items = (bookings || [])
    .map((b) => {
      const occBeg = toDayKey(pick(b, "dateBeg", "DateBeg"));
      const occEnd = toDayKey(pick(b, "dateEnd", "DateEnd"));
      if (!occBeg || !occEnd) return null;
      const dayBeg = indexOf.has(occBeg) ? indexOf.get(occBeg) : 0;
      const dayEnd = indexOf.has(occEnd) ? indexOf.get(occEnd) : last;
      if (dayEnd < dayBeg) return null;
      const timeBeg = pick(b, "timeBeg", "TimeBeg");
      const timeEnd = pick(b, "timeEnd", "TimeEnd");
      const hasCheckIn = timeBeg != null && timeBeg !== "";
      const hasCheckOut = timeEnd != null && timeEnd !== "";
      const start = dayBeg + timeOfDayOffset(timeBeg, hasCheckIn ? 0.5 : 0);
      let end = dayEnd + timeOfDayOffset(timeEnd, hasCheckOut ? 0.5 : 1);
      if (end <= start) end = start + MIN_BAR_DAYS;
      const idDoc = pick(b, "idDoc", "IdDoc");
      const { variant, statusText } = bookingAppearance(b);
      const idStatus = pick(b, "idStatus", "IdStatus") ?? 0;
      const syma = Number(pick(b, "syma", "Syma")) || 0;
      const symaPlat = Number(pick(b, "symaPlat", "SymaPlat")) || 0;
      return {
        idDoc,
        idNumber: pick(b, "idNumber", "IdNumber"),
        guestName: (pick(b, "guestName", "GuestName") || "").trim(),
        start,
        span: end - start,
        end,
        variant,
        statusText,
        idStatus,
        syma,
        hasDebt: syma - symaPlat > 0.005,
        dateBeg: timeBeg || occBeg,
        dateEnd: timeEnd || occEnd,
        roundStart: hasCheckIn,
        roundEnd: hasCheckOut
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start || b.span - a.span);

  const laneEnds = [];
  for (const item of items) {
    let lane = laneEnds.findIndex((end) => item.start >= end);
    if (lane < 0) {
      lane = laneEnds.length;
      laneEnds.push(item.end);
    } else {
      laneEnds[lane] = item.end;
    }
    item.lane = lane;
  }
  return { items, lanes: Math.max(1, laneEnds.length) };
};

const defaultFilters = () => ({
  dateBeg: dayjs().startOf("day").format("YYYY-MM-DD"),
  dateEnd: dayjs().add(13, "day").startOf("day").format("YYYY-MM-DD"),
  idBranch: getWorkplaceNumber("idBranch", 0),
  idNumberCats: [],
  idUpper: null
});

const SEARCH_DEBOUNCE_MS = 300;

const normalizePeriod = (beg, end) => {
  let dateBeg = beg;
  let dateEnd = end;
  if (dayjs(dateEnd).isBefore(dayjs(dateBeg), "day")) {
    [dateBeg, dateEnd] = [dateEnd, dateBeg];
  }
  if (dayjs(dateEnd).diff(dayjs(dateBeg), "day") > 62) {
    dateEnd = dayjs(dateBeg).add(62, "day").format("YYYY-MM-DD");
  }
  return { dateBeg, dateEnd };
};

const loadStoredFilters = () => {
  const base = defaultFilters();
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const hasExplicitBranch = Object.prototype.hasOwnProperty.call(parsed, "idBranch");
    let idBranch = Number(parsed.idBranch);
    if (!hasExplicitBranch || !Number.isFinite(idBranch) || idBranch < 0) {
      const legacy = Number(localStorage.getItem(BRANCH_STORAGE_KEY));
      idBranch = Number.isFinite(legacy) && legacy >= 0 ? legacy : base.idBranch;
    }
    const cats = Array.isArray(parsed.idNumberCats)
      ? parsed.idNumberCats.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0)
      : base.idNumberCats;
    const idUpperRaw = Number(parsed.idUpper);
    const idUpper = Number.isFinite(idUpperRaw) && idUpperRaw > 0 ? idUpperRaw : null;
    const { dateBeg, dateEnd } = normalizePeriod(
      isIsoDate(parsed.dateBeg) ? parsed.dateBeg : base.dateBeg,
      isIsoDate(parsed.dateEnd) ? parsed.dateEnd : base.dateEnd
    );
    return { dateBeg, dateEnd, idBranch, idNumberCats: cats, idUpper };
  } catch {
    return base;
  }
};

const saveStoredFilters = (filters) => {
  try {
    localStorage.setItem(
      FILTERS_STORAGE_KEY,
      JSON.stringify({
        dateBeg: filters.dateBeg,
        dateEnd: filters.dateEnd,
        idBranch: filters.idBranch,
        idNumberCats: filters.idNumberCats,
        idUpper: filters.idUpper
      })
    );
  } catch {
    /* ignore */
  }
};

const GROUP_DRAWER_WIDTH_KEY = "planOnline.bron.groupDrawer.width";
const groupDrawerMaxWidth = () => Math.min(1000, Math.max(320, window.innerWidth - 64));
const clampGroupDrawerWidth = (value) => Math.min(groupDrawerMaxWidth(), Math.max(320, Number(value) || 660));

const GroupBookingDrawer = ({ open, rooms: gridRooms, period, onClose, onCreated, onOpenDoc }) => {
  const [form] = Form.useForm();
  const [drawerSize, setDrawerSize] = useState(() => {
    try {
      return clampGroupDrawerWidth(localStorage.getItem(GROUP_DRAWER_WIDTH_KEY));
    } catch {
      return clampGroupDrawerWidth(660);
    }
  });
  const [lookups, setLookups] = useState({ vidPutevki: [], vidPitania: [] });
  const [active, setActive] = useState([]);
  const [created, setCreated] = useState([]);
  const [prices, setPrices] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [pasteNote, setPasteNote] = useState("");
  const guestsValue = Form.useWatch("guests", form);
  const [range, setRange] = useState(() => [dayjs(period.dateBeg), dayjs(period.dateEnd)]);
  const [freeMap, setFreeMap] = useState({});
  const [freeLoading, setFreeLoading] = useState(false);
  const freeRequest = useRef(0);
  const rooms = useMemo(
    () => gridRooms.map((r) => (freeMap[r.idNumber] ? { ...r, ...freeMap[r.idNumber] } : r)),
    [gridRooms, freeMap]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onResize = () => setDrawerSize((prev) => clampGroupDrawerWidth(prev));
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  const onDrawerResizeEnd = (size) => {
    const next = clampGroupDrawerWidth(typeof size === "number" ? size : drawerSize);
    setDrawerSize(next);
    try {
      localStorage.setItem(GROUP_DRAWER_WIDTH_KEY, String(next));
    } catch {}
  };

  useEffect(() => {
    if (!open) return;
    setActive(gridRooms.filter((r) => r.free > 0).map((r) => r.idNumber));
    setRange([dayjs(period.dateBeg), dayjs(period.dateEnd)]);
    setFreeMap({});
    loadFree([dayjs(period.dateBeg), dayjs(period.dateEnd)], true);
    setCreated([]);
    setPrices({});
    setError("");
    setPasteOpen(false);
    setPasteText("");
    setPasteNote("");
    form.resetFields();
    form.setFieldsValue({
      vidPutevki: undefined,
      vidPitania: undefined,
      timeBeg: dayjs("14:00", "HH:mm"),
      timeEnd: dayjs(period.dateBeg === period.dateEnd ? "18:00" : "12:00", "HH:mm"),
      guests: Object.fromEntries(gridRooms.map((r) => [`room_${r.idNumber}`, [{ name: "" }]]))
    });
    Promise.all([fetchDocExpLookups(), loadRuntimeSettings()])
      .then(([raw, settings]) => {
        const options = normalizeLookups(raw);
        setLookups(options);
        const put = getSetting(settings, "VID_PUT_DEFAULT", 0);
        const pit = getSetting(settings, "VID_PIT_DEFAULT", 0);
        form.setFieldsValue({
          vidPutevki: options.vidPutevki.some((o) => o.value === Number(put)) ? Number(put) : undefined,
          vidPitania: options.vidPitania.some((o) => o.value === Number(pit)) ? Number(pit) : undefined
        });
      })
      .catch((e) => setError(e.message || "Не удалось загрузить параметры путёвки"));
  }, [open, gridRooms, period, form]);

  function loadFree(next, selectAll) {
    const request = ++freeRequest.current;
    setFreeLoading(true);
    Promise.all(gridRooms.map((r) => fetchBronFreeMest({
      idNumber: r.idNumber,
      dateBeg: next[0].format("YYYY-MM-DD"),
      dateEnd: next[1].format("YYYY-MM-DD")
    }).then((res) => {
      const cap = Math.max(0, Number(res?.capacity ?? res?.Capacity) || 0);
      const isFree = Boolean(res?.isFree ?? res?.IsFree);
      return [r.idNumber, { cap, free: isFree ? cap : 0 }];
    })))
      .then((pairs) => {
        if (request !== freeRequest.current) return;
        const map = Object.fromEntries(pairs);
        setFreeMap(map);
        setActive((prev) => (selectAll ? gridRooms.map((r) => r.idNumber) : prev).filter((id) => map[id]?.free > 0));
      })
      .catch((e) => { if (request === freeRequest.current) setError(e.message || "Не удалось проверить свободные места"); })
      .finally(() => { if (request === freeRequest.current) setFreeLoading(false); });
  }

  const changeRange = (next) => {
    if (!next?.[0] || !next?.[1]) return;
    setRange(next);
    setError("");
    loadFree(next, false);
  };

  const buildBookings = async () => {
    const values = await form.validateFields();
    if (!active.length) throw new Error("Выберите хотя бы один свободный номер");
    if (freeLoading) throw new Error("Дождитесь проверки свободных мест");
    if (overfilled.length) throw new Error(`Гостей больше, чем свободных мест: ${overfilled.map((r) => r.name).join(", ")}`);
    const beg = range[0].startOf("day").hour(values.timeBeg.hour()).minute(values.timeBeg.minute());
    const end = range[1].startOf("day").hour(values.timeEnd.hour()).minute(values.timeEnd.minute());
    if (!end.isAfter(beg)) throw new Error("Выезд должен быть позже заезда");
    return active.map((idNumber) => ({
      idNumber,
      dateBeg: toApiDateTime(beg),
      dateEnd: toApiDateTime(end),
      vidPutevki: values.vidPutevki,
      vidPitania: values.vidPitania,
      komment: values.komment || "",
      clients: (values.guests?.[`room_${idNumber}`] || []).map((guest) => ({
        name: guest.name.trim(), vidMesta: 0, isBaby: 0
      }))
    }));
  };

  const watchPut = Form.useWatch("vidPutevki", form);
  const watchPit = Form.useWatch("vidPitania", form);
  const watchTimeBeg = Form.useWatch("timeBeg", form);
  const watchTimeEnd = Form.useWatch("timeEnd", form);
  const priceKey = useMemo(() => {
    const at = (d, t) => (t?.isValid?.() ? toApiDateTime(d.startOf("day").hour(t.hour()).minute(t.minute())) : "");
    return JSON.stringify({
      put: watchPut || 0,
      pit: watchPit || 0,
      beg: at(range[0], watchTimeBeg),
      end: at(range[1], watchTimeEnd),
      rooms: active.map((id) => [id, guestsValue?.[`room_${id}`]?.length || 0])
    });
  }, [watchPut, watchPit, watchTimeBeg, watchTimeEnd, range, active, guestsValue]);

  useEffect(() => {
    if (!open) return undefined;
    const p = JSON.parse(priceKey);
    if (!p.put || !p.pit || !p.beg || !p.end || !p.rooms.length) {
      setPrices({});
      setChecking(false);
      return undefined;
    }
    const ac = new AbortController();
    const t = setTimeout(() => {
      setChecking(true);
      Promise.all(p.rooms.map(([idNumber, count]) => docExpSellBookPreview({
        idNumber,
        dateBeg: p.beg,
        dateEnd: p.end,
        vidPutevki: p.put,
        vidPitania: p.pit,
        clients: Array.from({ length: Math.max(1, count) }, () => ({ vidMesta: 0, isBaby: 0 }))
      }, ac.signal)
        .then((res) => [idNumber, res])
        .catch((e) => [idNumber, { error: e.message || "Не удалось рассчитать стоимость" }])))
        .then((pairs) => { if (!ac.signal.aborted) setPrices(Object.fromEntries(pairs)); })
        .finally(() => { if (!ac.signal.aborted) setChecking(false); });
    }, 400);
    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, [open, priceKey]);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const bookings = await buildBookings();
      const result = await docExpSellBookGroup(bookings);
      const saved = result.created || [];
      setCreated((previous) => [...previous, ...saved]);
      setActive((previous) => previous.filter((id) => !saved.some((item) => item.idNumber === id)));
      onCreated();
      if (result.error) setError(`Номер ${rooms.find((r) => r.idNumber === result.failedNumber)?.name || result.failedNumber}: ${result.error}`);
      else message.success(`Создано путёвок: ${saved.length}`);
    } catch (e) {
      if (!e?.errorFields) setError(e.message || "Не удалось создать путёвки");
    } finally {
      setBusy(false);
    }
  };

  const total = active.length && active.every((id) => prices[id] && !prices[id].error)
    ? active.reduce((sum, id) => sum + Number(prices[id].syma || 0), 0)
    : null;
  const priceErrors = active.filter((id) => prices[id]?.error).length;
  const sumOf = (key) => active.reduce((sum, id) => sum + Number(prices[id]?.[key] || 0), 0);
  const money = (v) => `${Number(v || 0).toLocaleString("ru-RU", { maximumFractionDigits: 2 })} ₽`;
  const breakdown = (src) => <div className="bron-group__breakdown">
    <span>Проживание</span><b>{money(src.symaAcc)}</b>
    <span>Питание</span><b>{money(src.symaPit)}</b>
    <span>Лечение</span><b>{money(src.symaLech)}</b>
    {Number(src.discount) > 0 && <><span>Скидка</span><b>−{money(src.discount)}</b></>}
  </div>;
  const nights = range[1].startOf("day").diff(range[0].startOf("day"), "day");
  const selectable = rooms.filter((r) => r.free > 0 && !created.some((c) => c.idNumber === r.idNumber));
  const guestCount = active.reduce((sum, id) => sum + (guestsValue?.[`room_${id}`]?.length || 0), 0);
  const overfilled = rooms.filter((r) => active.includes(r.idNumber)
    && (guestsValue?.[`room_${r.idNumber}`]?.length || 0) > r.free);
  const seatCount = rooms.filter((r) => active.includes(r.idNumber)).reduce((sum, r) => sum + r.free, 0);

  const toggleRoom = (idNumber, checked) => {
    setActive((prev) => (checked ? [...prev, idNumber] : prev.filter((id) => id !== idNumber)));
  };

  const applyPaste = () => {
    const names = pasteText.split(/\r?\n|;/).map((s) => s.trim()).filter(Boolean);
    if (!names.length) return;
    const next = { ...(form.getFieldValue("guests") || {}) };
    let i = 0;
    rooms.filter((r) => active.includes(r.idNumber)).forEach((r) => {
      const take = names.slice(i, i + r.free);
      i += take.length;
      if (take.length) next[`room_${r.idNumber}`] = take.map((name) => ({ name }));
    });
    form.setFieldsValue({ guests: next });
    setPasteNote(i < names.length ? `Не поместилось гостей: ${names.length - i}` : `Распределено гостей: ${i}`);
    if (i === names.length) {
      setPasteOpen(false);
      setPasteText("");
    }
  };

  return (
    <Drawer
      title={<span className="bron-group__title">Групповое бронирование<small>отдельная путёвка на каждый номер</small></span>}
      placement="right" size={drawerSize}
      maxSize={groupDrawerMaxWidth()} resizable={{ onResize: (size) => setDrawerSize(clampGroupDrawerWidth(size)), onResizeEnd: onDrawerResizeEnd }}
      open={open} onClose={onClose} maskClosable={!busy} keyboard={!busy}
      closable={busy ? false : { placement: "end" }} rootClassName="bron-group-drawer"
      footer={<div className="bron-group__footer">
        <div className="bron-group__footer-stats">
          <span><b>{active.length}</b> номеров</span>
          <span><b>{guestCount}</b> гостей</span>
          {checking ? <span className="bron-group__footer-sum is-empty">расчёт…</span>
            : priceErrors ? <span className="bron-group__footer-sum is-error">Ошибок расчёта: {priceErrors}</span>
              : total != null ? <Tooltip title={breakdown({
                symaAcc: sumOf("symaAcc"), symaPit: sumOf("symaPit"), symaLech: sumOf("symaLech"), discount: sumOf("discount")
              })}>
                <span className="bron-group__footer-sum">{money(total)}</span>
              </Tooltip>
                : <span className="bron-group__footer-sum is-empty">Укажите условия для расчёта</span>}
        </div>
        <div className="bron-group__footer-actions">
          <Button type="primary" onClick={submit} loading={busy}
            disabled={!active.length || freeLoading || overfilled.length > 0}
            title={overfilled.length ? "В некоторых номерах гостей больше, чем свободных мест" : undefined}>
            Создать путёвки ({active.length})
          </Button>
        </div>
      </div>}
    >
      {error && <Alert type="error" showIcon message={error} className="bron-group__notice" />}
      {created.length > 0 && <Alert type="success" showIcon className="bron-group__notice"
        message={`Создано путёвок: ${created.length}`}
        description={<div className="bron-group__created">{created.map((item) =>
          <button type="button" key={item.idDoc} onClick={() => onOpenDoc(item.idDoc)}>
            {rooms.find((r) => r.idNumber === item.idNumber)?.name} · №{item.idDoc}
          </button>)}</div>} />}

      <Form form={form} layout="vertical" requiredMark={false}>
        <section className="bron-group__card">
          <h4 className="bron-group__card-title"><span>1</span>Условия для всех</h4>
          <div className="bron-group__shared">
            <div className="bron-group__field">
              <span className="bron-group__field-label">Заезд</span>
              <div className="bron-group__pair">
                <DatePicker size="small" value={range[0]} format="DD.MM.YY dd" allowClear={false} disabled={busy}
                  suffixIcon={null} onChange={(d) => d && changeRange([d, d.add(Math.max(nights, 0), "day")])} />
                <Form.Item name="timeBeg" noStyle rules={[{ required: true }]}>
                  <TimePicker size="small" format="HH:mm" allowClear={false} needConfirm={false} minuteStep={5} suffixIcon={null} />
                </Form.Item>
              </div>
            </div>
            <div className="bron-group__field">
              <span className="bron-group__field-label bron-group__period-label">
                Выезд
                <em>{freeLoading ? "проверка мест…" : `${nights} ${nights % 10 === 1 && nights % 100 !== 11 ? "ночь"
                  : [2, 3, 4].includes(nights % 10) && ![12, 13, 14].includes(nights % 100) ? "ночи" : "ночей"}`}</em>
              </span>
              <div className="bron-group__pair">
                <DatePicker size="small" value={range[1]} format="DD.MM.YY dd" allowClear={false} disabled={busy}
                  suffixIcon={null} disabledDate={(d) => d.isBefore(range[0], "day")}
                  onChange={(d) => d && changeRange([range[0], d])} />
                <Form.Item name="timeEnd" noStyle rules={[{ required: true }]}>
                  <TimePicker size="small" format="HH:mm" allowClear={false} needConfirm={false} minuteStep={5} suffixIcon={null} />
                </Form.Item>
              </div>
            </div>
            <Form.Item name="vidPutevki" label="Вид путёвки" rules={[{ required: true, message: "Укажите вид путёвки" }]}>
              <Select options={(lookups.vidPutevki || []).filter((o) => o.value)} size="small" placeholder="Выберите" showSearch optionFilterProp="label" />
            </Form.Item>
            <Form.Item name="vidPitania" label="Питание" rules={[{ required: true, message: "Укажите питание" }]}>
              <Select options={(lookups.vidPitania || []).filter((o) => o.value)} size="small" placeholder="Выберите" showSearch optionFilterProp="label" />
            </Form.Item>
            <Form.Item name="komment" label="Комментарий" className="bron-group__span2">
              <Input size="small" placeholder="Для всех путёвок, необязательно" />
            </Form.Item>
          </div>
        </section>

        <section className="bron-group__card">
          <div className="bron-group__card-head">
            <h4 className="bron-group__card-title"><span>2</span>Номера и гости</h4>
            <div className="bron-group__tools">
              <button type="button" disabled={busy} onClick={() => { setActive(selectable.map((r) => r.idNumber)); }}>Все</button>
              <button type="button" disabled={busy} onClick={() => setActive([])}>Сбросить</button>
              <button type="button" disabled={busy || !active.length} className={pasteOpen ? "is-on" : ""}
                onClick={() => { setPasteOpen((v) => !v); setPasteNote(""); }}>Вставить список</button>
            </div>
          </div>

          {pasteOpen && <div className="bron-group__paste">
            <Input.TextArea size="small" autoSize={{ minRows: 2, maxRows: 6 }} value={pasteText} onChange={(e) => setPasteText(e.target.value)}
              placeholder={"Иванов Иван\nПетрова Анна\n…по одному гостю в строке"} />
            <div className="bron-group__paste-foot">
              <span>{pasteNote || `Распределим по номерам по порядку · мест: ${seatCount}`}</span>
              <Button size="small" type="primary" onClick={applyPaste} disabled={!pasteText.trim()}>Распределить</Button>
            </div>
          </div>}

          <div className="bron-group__rooms">
            {rooms.map((room) => {
              const done = created.some((item) => item.idNumber === room.idNumber);
              const enabled = active.includes(room.idNumber);
              const used = enabled ? guestsValue?.[`room_${room.idNumber}`]?.length || 0 : 0;
              const price = prices[room.idNumber];
              const cap = Math.max(room.cap || 0, room.free);
              const over = enabled && used > room.free;
              const state = freeLoading ? "checking" : room.free <= 0 ? "busy" : "free";
              const stateText = { checking: "проверка…", busy: "Занят", free: "Свободен" }[state];
              const cls = done ? " is-done" : over ? " is-over" : room.free <= 0 ? " is-full" : enabled ? " is-on" : "";
              return <article className={`bron-group__room${cls}`} key={room.idNumber}>
                <label className="bron-group__room-head">
                  <Checkbox checked={enabled} disabled={busy || done || room.free <= 0}
                    onChange={(e) => toggleRoom(room.idNumber, e.target.checked)} />
                  <span className="bron-group__room-badge">{room.name}</span>
                  <span className="bron-group__room-info">
                    <span className="bron-group__room-cat">{room.cat || "Без категории"}</span>
                    <span className={`bron-group__avail is-${done ? "done" : state}`}>{done ? "Путёвка создана" : stateText}</span>
                    <span className="bron-group__seats" aria-label={`Мест: ${cap}, гостей: ${used}`}>
                      {Array.from({ length: Math.min(cap, 8) }, (_, i) =>
                        <i key={i} className={i < used ? "is-taken" : ""} />)}
                      {cap > 8 && <em>+{cap - 8}</em>}
                    </span>
                  </span>
                  {enabled && !done && <span className={`bron-group__room-count${over ? " is-over" : ""}`}
                    title={over ? "Гостей больше, чем свободных мест" : undefined}>{used}/{room.free}</span>}
                  <span className={`bron-group__room-status${enabled && checking ? " is-loading" : ""}`}>
                    {done || room.free <= 0 || !enabled ? ""
                      : price?.error ? <Tooltip title={price.error}><span className="is-error">Ошибка</span></Tooltip>
                        : price ? <Tooltip title={<>{breakdown(price)}<div className="bron-group__breakdown-days">
                          {price.days} сут. · {money(price.symaDay)} в сутки</div></>}>
                          <span>{money(price.syma)}</span>
                        </Tooltip>
                          : checking ? "…" : "—"}
                  </span>
                </label>
                {over && <div className="bron-group__over">
                  В номере {room.free} мест. Уберите лишних гостей.
                </div>}
                {enabled && <Form.List name={["guests", `room_${room.idNumber}`]}>
                  {(fields, { add, remove }) => <div className="bron-group__guests">
                    {fields.map((field, index) => <div className="bron-group__guest" key={field.key}>
                      <span className="bron-group__guest-num">{index + 1}</span>
                      <Form.Item name={[field.name, "name"]}
                        rules={[{ required: true, whitespace: true, message: "Введите ФИО" }]}>
                        <Input size="small" placeholder="ФИО гостя" maxLength={150} variant="borderless"
                          aria-label={`Гость ${index + 1}, номер ${room.name}`}
                          onPressEnter={(e) => {
                            e.preventDefault();
                            if (index === fields.length - 1 && fields.length < room.free) add({ name: "" });
                          }} />
                      </Form.Item>
                      {fields.length > 1 && <button type="button" className="bron-group__guest-del" onClick={() => remove(field.name)}
                        aria-label={`Убрать гостя ${index + 1} из номера ${room.name}`}>×</button>}
                    </div>)}
                    {fields.length < room.free && <button type="button" className="bron-group__add" onClick={() => add({ name: "" })}>
                      + Гость
                    </button>}
                  </div>}
                </Form.List>}
              </article>;
            })}
          </div>
        </section>
      </Form>
    </Drawer>
  );
};

const minDay = (a, b) => (dayjs(a).isBefore(dayjs(b), "day") ? a : b);
const maxDay = (a, b) => (dayjs(a).isAfter(dayjs(b), "day") ? a : b);

/**
 * Календарь брони. Delphi: TFrmActRezerv (law 11).
 * Выделение — drag по ячейкам (дни × номера).
 */
const BronCalendar = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const idDocParam = Number(searchParams.get("idDoc")) || null;
  /** Путёвка, для которой выбирается новое размещение (перебронь из шахматки). */
  const [rebook, setRebook] = useState(null);
  const activeDocId = rebook?.idDoc || idDocParam;
  const law = useSelector((state) => state.user.session?.law);
  const canView = getLawFlag(law, 11) === 1;
  const canAdd = getLawFlag(law, 30) === 1;
  const canEdit = getLawFlag(law, 32) === 1;

  const storedFilters = useMemo(() => loadStoredFilters(), []);
  const initialPeriod = useMemo(
    () => {
      const urlBeg = searchParams.get("dateBeg");
      const urlEnd = searchParams.get("dateEnd");
      return normalizePeriod(
        isIsoDate(urlBeg) && dayjs(urlBeg).isValid() ? urlBeg : storedFilters.dateBeg,
        isIsoDate(urlEnd) && dayjs(urlEnd).isValid() ? urlEnd : storedFilters.dateEnd
      );
    },
    []
  );
  const [dateBeg, setDateBeg] = useState(() => dayjs(initialPeriod.dateBeg).startOf("day"));
  const [dateEnd, setDateEnd] = useState(() => dayjs(initialPeriod.dateEnd).startOf("day"));
  const [idBranch, setIdBranch] = useState(storedFilters.idBranch);
  const [idNumberCats, setIdNumberCats] = useState(storedFilters.idNumberCats);
  const [idUpper, setIdUpper] = useState(storedFilters.idUpper);
  const [searchQ, setSearchQ] = useState(() => searchParams.get("q") || "");
  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [groupTree, setGroupTree] = useState([]);
  const [numberById, setNumberById] = useState(() => new Map());
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  /** { rowFrom, rowTo, dateBeg, dateEnd } — индексы строк в numbers */
  const [selection, setSelection] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [sellOpen, setSellOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupPreset, setGroupPreset] = useState(null);
  const [viewDocId, setViewDocId] = useState(null);

  const dragRef = useRef(null);
  /** Перетаскивание полоски путёвки: { mode, booking, anchorDate, originRow, moved } */
  const bookingDragRef = useRef(null);
  const suppressMenuRef = useRef(false);
  /** Предпросмотр нового размещения: { row, dateBeg, dateEnd, idDoc, variant } */
  const [dragPreview, setDragPreviewState] = useState(null);
  const dragPreviewRef = useRef(null);
  const setDragPreview = useCallback((next) => {
    dragPreviewRef.current = next;
    setDragPreviewState(next);
  }, []);
  const [menuOpenKey, setMenuOpenKey] = useState(null);
  const selectionRef = useRef(null);
  selectionRef.current = selection;

  useEffect(() => {
    saveStoredFilters({
      dateBeg: dateBeg.format("YYYY-MM-DD"),
      dateEnd: dateEnd.format("YYYY-MM-DD"),
      idBranch,
      idNumberCats,
      idUpper
    });
  }, [dateBeg, dateEnd, idBranch, idNumberCats, idUpper]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("dateBeg", dateBeg.format("YYYY-MM-DD"));
    next.set("dateEnd", dateEnd.format("YYYY-MM-DD"));
    const trimmed = searchQ.trim();
    if (trimmed) {
      next.set("q", trimmed);
    } else {
      next.delete("q");
    }
    if (next.toString() === searchParams.toString()) return undefined;
    const delay = (searchParams.get("q") || "") !== trimmed ? SEARCH_DEBOUNCE_MS : 0;
    const t = setTimeout(() => setSearchParams(next, { replace: true }), delay);
    return () => clearTimeout(t);
  }, [dateBeg, dateEnd, searchQ, searchParams, setSearchParams]);

  useEffect(() => {
    Promise.all([
      fetchSprNumberBranches(),
      fetchSprNumberCategories(),
      fetchSprNumberList(idBranch || undefined)
    ])
      .then(([br, cats, numbers]) => {
        setBranches(
          [{ value: 0, label: "Все филиалы" }].concat(
            (Array.isArray(br) ? br : []).map((b) => ({
              value: pick(b, "id", "Id", "value", "Value", "idBranch", "IdBranch") || 0,
              label: pick(b, "name", "Name", "label", "Label")
            }))
          )
        );
        setCategories(
          (Array.isArray(cats) ? cats : [])
            .map((c) => ({
              value: pick(c, "idNumberCat", "IdNumberCat", "value") || 0,
              label: pick(c, "name", "Name", "label")
            }))
            .filter((c) => c.value > 0)
        );
        const list = Array.isArray(numbers) ? numbers : pick(numbers, "items", "Items") || [];
        setGroupTree(buildGroupTree(list));
        const byId = new Map();
        list.forEach((row) => {
          const n = normalizeRow(row);
          if (n.idNumber != null) byId.set(n.idNumber, n);
        });
        setNumberById(byId);
      })
      .catch(() => {});
  }, [idBranch]);

  const groupTreeData = useMemo(
    () =>
      (groupTree || []).map(function mapNode(node) {
        return {
          value: node.idNumber,
          title: node.title || node.name,
          key: node.key || String(node.idNumber),
          children: node.children?.length ? node.children.map(mapNode) : undefined
        };
      }),
    [groupTree]
  );

  const load = useCallback(() => {
    if (!canView) return;
    setLoading(true);
    setError("");
    fetchBronGrid({
      dateBeg: dateBeg.format("YYYY-MM-DD"),
      dateEnd: dateEnd.format("YYYY-MM-DD"),
      idBranch: idBranch || undefined,
      idNumberCat: idNumberCats,
      idUpper: idUpper || undefined
    })
      .then((data) => {
        setGrid({
          numbers: pick(data, "numbers", "Numbers") || [],
          cells: pick(data, "cells", "Cells") || [],
          bookings: pick(data, "bookings", "Bookings") || []
        });
        setSelection(null);
      })
      .catch((e) => setError(e.message || "Не удалось загрузить сетку"))
      .finally(() => setLoading(false));
  }, [canView, dateBeg, dateEnd, idBranch, idNumberCats, idUpper]);

  useEffect(() => {
    load();
  }, [load]);

  const dates = useMemo(() => {
    const list = [];
    for (let d = dateBeg; !d.isAfter(dateEnd, "day"); d = d.add(1, "day")) {
      list.push(d);
    }
    return list;
  }, [dateBeg, dateEnd]);

  const allNumbers = useMemo(() => grid?.numbers || [], [grid]);

  const cellMap = useMemo(() => {
    const map = new Map();
    for (const c of grid?.cells || []) {
      const idNumber = pick(c, "idNumber", "IdNumber");
      const fdate = dayjs(pick(c, "fdate", "Fdate")).format("YYYY-MM-DD");
      map.set(`${idNumber}|${fdate}`, c);
    }
    return map;
  }, [grid]);

  const bookingsByRoom = useMemo(() => {
    const dateKeys = dates.map((d) => d.format("YYYY-MM-DD"));
    const grouped = new Map();
    for (const b of grid?.bookings || []) {
      const idNumber = pick(b, "idNumber", "IdNumber");
      if (!grouped.has(idNumber)) grouped.set(idNumber, []);
      grouped.get(idNumber).push(b);
    }
    const laid = new Map();
    for (const [idNumber, list] of grouped) {
      laid.set(idNumber, layoutRoomBookings(list, dateKeys));
    }
    return laid;
  }, [grid, dates]);

  const numbers = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return allNumbers;
    return allNumbers.filter((n) => {
      const idNumber = pick(n, "idNumber", "IdNumber");
      const nameRaw = pick(n, "name", "Name") || "";
      const name = String(nameRaw).toLowerCase();
      const cat = String(pick(n, "catName", "CatName") || "").toLowerCase();
      const roomUpper = pick(n, "idUpper", "IdUpper") || 0;
      const path = pathFromSelectedToRoom(nameRaw, roomUpper, idUpper, numberById).toLowerCase();
      if (name.includes(q) || cat.includes(q) || path.includes(q)) return true;
      const guests = bookingsByRoom.get(idNumber)?.items || [];
      if (guests.some((b) => (b.guestName || "").toLowerCase().includes(q))) return true;
      return dates.some((d) => {
        const cell = cellMap.get(`${idNumber}|${d.format("YYYY-MM-DD")}`);
        const guest = String(pick(cell, "guestName", "GuestName") || "").toLowerCase();
        return guest.includes(q);
      });
    });
  }, [allNumbers, searchQ, bookingsByRoom, cellMap, dates, idUpper, numberById]);

  useEffect(() => {
    setSelection(null);
  }, [searchQ]);

  const patchSearchFilters = (next) => {
    if (Object.prototype.hasOwnProperty.call(next, "q")) setSearchQ(next.q || "");
    if (Object.prototype.hasOwnProperty.call(next, "idBranch")) setIdBranch(next.idBranch);
    if (Object.prototype.hasOwnProperty.call(next, "idUpper")) setIdUpper(next.idUpper);
    if (Object.prototype.hasOwnProperty.call(next, "idNumberCats")) setIdNumberCats(next.idNumberCats);
  };

  const applyDragSelection = useCallback((anchor, current) => {
    if (!anchor || !current) return;
    setSelection({
      rowFrom: Math.min(anchor.row, current.row),
      rowTo: Math.max(anchor.row, current.row),
      dateBeg: minDay(anchor.date, current.date),
      dateEnd: maxDay(anchor.date, current.date)
    });
  }, []);

  const cellFromPoint = useCallback((clientX, clientY) => {
    const el = document.elementFromPoint(clientX, clientY);
    const td = el?.closest?.(".bron-cell");
    if (!td) return null;
    const row = Number(td.dataset.row);
    const date = td.dataset.date;
    if (!Number.isFinite(row) || !date) return null;
    return { row, date };
  }, []);

  const previewFromPointer = useCallback((drag, cur) => {
    const b = drag.booking;
    const beg0 = dayjs(b.dateBeg).startOf("day");
    const end0 = dayjs(b.dateEnd).startOf("day");
    const curDay = dayjs(cur.date);
    if (drag.mode === "move") {
      const offset = curDay.diff(dayjs(drag.anchorDate), "day");
      return {
        row: cur.row,
        dateBeg: beg0.add(offset, "day").format("YYYY-MM-DD"),
        dateEnd: end0.add(offset, "day").format("YYYY-MM-DD")
      };
    }
    if (drag.mode === "resize-end") {
      const end = curDay.isBefore(beg0, "day") ? beg0 : curDay;
      return { row: drag.originRow, dateBeg: beg0.format("YYYY-MM-DD"), dateEnd: end.format("YYYY-MM-DD") };
    }
    const beg = curDay.isAfter(end0, "day") ? end0 : curDay;
    return { row: drag.originRow, dateBeg: beg.format("YYYY-MM-DD"), dateEnd: end0.format("YYYY-MM-DD") };
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      const bd = bookingDragRef.current;
      if (bd) {
        const cur = cellFromPoint(e.clientX, e.clientY);
        if (!cur) return;
        const next = previewFromPointer(bd, cur);
        const same = next.row === bd.originRow
          && next.dateBeg === dayjs(bd.booking.dateBeg).format("YYYY-MM-DD")
          && next.dateEnd === dayjs(bd.booking.dateEnd).format("YYYY-MM-DD");
        if (!same && !bd.moved) {
          bd.moved = true;
          document.body.classList.add("bron-calendar--dragging");
        }
        if (!bd.moved) return;
        setDragPreview({ ...next, idDoc: bd.booking.idDoc, variant: bd.booking.variant, changed: !same });
        return;
      }
      const drag = dragRef.current;
      if (!drag?.anchor) return;
      const cur = cellFromPoint(e.clientX, e.clientY);
      if (!cur) return;
      applyDragSelection(drag.anchor, cur);
    };
    const endDrag = () => {
      if (bookingDragRef.current) {
        const bd = bookingDragRef.current;
        bookingDragRef.current = null;
        document.body.classList.remove("bron-calendar--dragging");
        if (bd.moved) {
          suppressMenuRef.current = true;
          setTimeout(() => { suppressMenuRef.current = false; }, 300);
        }
        const preview = dragPreviewRef.current;
        setDragPreview(null);
        if (preview?.changed) finishBookingDragRef.current?.(bd.booking, preview);
        return;
      }
      if (dragRef.current) {
        dragRef.current = null;
        document.body.classList.remove("bron-calendar--dragging");
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
      document.body.classList.remove("bron-calendar--dragging");
    };
  }, [applyDragSelection, cellFromPoint, previewFromPointer, setDragPreview]);

  const finishBookingDragRef = useRef(null);
  finishBookingDragRef.current = async (b, preview) => {
    const target = numbers[preview.row];
    const idNumber = target ? pick(target, "idNumber", "IdNumber") : null;
    if (!idNumber) return;
    const beg0 = dayjs(b.dateBeg);
    const end0 = dayjs(b.dateEnd);
    const dateBeg = dayjs(preview.dateBeg).hour(beg0.hour()).minute(beg0.minute());
    const dateEnd = dayjs(preview.dateEnd).hour(end0.hour()).minute(end0.minute());
    if (!dateEnd.isAfter(dateBeg)) {
      message.warning("Выезд должен быть позже заезда");
      return;
    }
    const oldNumber = numbers.find((n) => pick(n, "idNumber", "IdNumber") === b.idNumber);
    const roomChanged = idNumber !== b.idNumber;
    const nights0 = end0.startOf("day").diff(beg0.startOf("day"), "day");
    const nights1 = dateEnd.startOf("day").diff(dateBeg.startOf("day"), "day");
    const fmt = (d) => d.format("DD.MM.YYYY");
    const lines = [
      `${b.guestName || "Гость не указан"}`,
      roomChanged
        ? `Номер: ${pick(oldNumber, "name", "Name") || "—"} → ${pick(target, "name", "Name")}`
        : `Номер: ${pick(target, "name", "Name")}`,
      `Заезд: ${fmt(beg0)}${beg0.isSame(dateBeg, "day") ? "" : ` → ${fmt(dateBeg)}`}`,
      `Выезд: ${fmt(end0)}${end0.isSame(dateEnd, "day") ? "" : ` → ${fmt(dateEnd)}`}`,
      nights0 !== nights1 ? `Ночей: ${nights0} → ${nights1}` : `Ночей: ${nights1}`
    ];
    Modal.confirm({
      title: roomChanged || nights0 === nights1
        ? `Переместить путёвку №${b.idDoc}?`
        : `Изменить срок проживания путёвки №${b.idDoc}?`,
      content: <div className="bron-calendar__confirm">{lines.map((l) => <div key={l}>{l}</div>)}</div>,
      okText: "Применить",
      cancelText: "Отмена",
      onOk: async () => {
        try {
          await docExpBook(b.idDoc, {
            idNumber,
            dateBeg: toApiDateTime(dateBeg),
            dateEnd: toApiDateTime(dateEnd),
            rebook: true
          });
          message.success(`Путёвка №${b.idDoc}: ${pick(target, "name", "Name")}, ${dateBeg.format("DD.MM")}–${dateEnd.format("DD.MM")}`);
        } catch (e) {
          message.error(e.message || "Не удалось переместить путёвку");
        } finally {
          load();
        }
      }
    });
  };

  const onBookingPointerDown = (e, b, row, mode) => {
    if (e.button != null && e.button !== 0) return;
    const cur = cellFromPoint(e.clientX, e.clientY);
    e.stopPropagation();
    e.preventDefault();
    bookingDragRef.current = {
      mode,
      booking: b,
      originRow: row,
      anchorDate: cur?.date || dayjs(b.dateBeg).format("YYYY-MM-DD"),
      moved: false
    };
  };

  const onCellPointerDown = (e, row, date) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    const anchor = { row, date };
    dragRef.current = { anchor };
    document.body.classList.add("bron-calendar--dragging");
    applyDragSelection(anchor, anchor);
  };

  const isCellSelected = (row, date) => {
    if (!selection) return false;
    if (row < selection.rowFrom || row > selection.rowTo) return false;
    const d = dayjs(date);
    return !d.isBefore(dayjs(selection.dateBeg), "day") && !d.isAfter(dayjs(selection.dateEnd), "day");
  };

  const singleRoomId = useMemo(() => {
    if (!selection || selection.rowFrom !== selection.rowTo) return null;
    const n = numbers[selection.rowFrom];
    return n ? pick(n, "idNumber", "IdNumber") : null;
  }, [selection, numbers]);

  const selectedDaysCount = useMemo(() => {
    if (!selection) return 0;
    return dayjs(selection.dateEnd).diff(dayjs(selection.dateBeg), "day") + 1;
  }, [selection]);

  const selectedRoomsCount = useMemo(() => {
    if (!selection) return 0;
    return selection.rowTo - selection.rowFrom + 1;
  }, [selection]);

  const selectedFree = useMemo(() => {
    if (!selection || !singleRoomId) return false;
    for (
      let d = dayjs(selection.dateBeg);
      !d.isAfter(dayjs(selection.dateEnd), "day");
      d = d.add(1, "day")
    ) {
      const cell = cellMap.get(`${singleRoomId}|${d.format("YYYY-MM-DD")}`);
      const state = pick(cell, "state", "State");
      if (state === 2 || state === 3) return false;
    }
    return true;
  }, [selection, singleRoomId, cellMap]);

  const selectedGroupRooms = useMemo(() => {
    if (!selection || selection.rowFrom === selection.rowTo) return [];
    return numbers.slice(selection.rowFrom, selection.rowTo + 1).map((n) => {
      const idNumber = pick(n, "idNumber", "IdNumber");
      let free = Infinity;
      for (let d = dayjs(selection.dateBeg); !d.isAfter(dayjs(selection.dateEnd), "day"); d = d.add(1, "day")) {
        const cell = cellMap.get(`${idNumber}|${d.format("YYYY-MM-DD")}`);
        free = Math.min(free, Number(pick(cell, "freeAll", "FreeAll")) || 0);
      }
      return {
        idNumber,
        name: pick(n, "name", "Name") || String(idNumber),
        cat: pick(n, "catName", "CatName") || "",
        cap: Number(pick(n, "cntMest", "CntMest")) || 0,
        free
      };
    });
  }, [selection, numbers, cellMap]);

  const startRebook = (b) => {
    setRebook({ idDoc: b.idDoc, guestName: b.guestName });
    setSelection(null);
    message.info("Выделите новый номер и период, затем нажмите «Бронь»");
  };

  const clearPlacement = (b) => {
    Modal.confirm({
      title: `Очистить размещение путёвки №${b.idDoc}?`,
      content: "Номер и даты будут сняты, путёвка останется в журнале без размещения.",
      okText: "Очистить",
      okButtonProps: { danger: true },
      cancelText: "Отмена",
      onOk: async () => {
        try {
          await docExpClearPlacement(b.idDoc);
          load();
          Modal.confirm({
            title: "Размещение очищено",
            content: "Разместить путёвку заново на шахматке?",
            okText: "Разместить",
            cancelText: "Нет",
            onOk: () => startRebook(b)
          });
        } catch (e) {
          message.error(e.message || "Не удалось очистить размещение");
        }
      }
    });
  };

  if (!canView) {
    return (
      <div className="bron-calendar">
        <Alert type="warning" showIcon message="Нет права просмотра брони (law 11)" />
      </div>
    );
  }

  return (
    <div className="bron-calendar">
      <div className="bron-calendar__filters">
        <div className="bron-calendar__field">
          <DatePicker
            value={dateBeg}
            format="DD.MM.YYYY"
            suffixIcon={null}
            title="Дата с"
            aria-label="Дата с"
            onChange={(d) => d && setDateBeg(d.startOf("day"))}
            allowClear={false}
          />
        </div>
        <div className="bron-calendar__field">
          <DatePicker
            value={dateEnd}
            format="DD.MM.YYYY"
            suffixIcon={null}
            title="Дата по"
            aria-label="Дата по"
            onChange={(d) => d && setDateEnd(d.startOf("day"))}
            allowClear={false}
          />
        </div>
        <BronSearchFilter
          q={searchQ}
          idBranch={idBranch}
          idUpper={idUpper}
          idNumberCats={idNumberCats}
          branches={branches}
          categories={categories}
          groupTreeData={groupTreeData}
          onChange={patchSearchFilters}
        />
        <div className="bron-calendar__extra-actions">
          <Tooltip
            title={
              selectedRoomsCount > 1
                ? "Создать отдельную путёвку на каждый выбранный номер"
                : activeDocId
                  ? "Разместить выбранную путёвку на выделенный номер и период"
                  : "Для одного номера откройте путёвку из журнала или используйте «Продать»"
            }
          >
            <Button
              type="primary"
              disabled={selectedRoomsCount > 1
                ? !canAdd || selectedGroupRooms.length > 30 || !selectedGroupRooms.some((r) => r.free > 0)
                : !canEdit || !singleRoomId || !activeDocId || !selectedFree}
              onClick={() => {
                if (selectedRoomsCount > 1) {
                  setGroupPreset({ rooms: selectedGroupRooms, dateBeg: selection.dateBeg, dateEnd: selection.dateEnd });
                  setGroupOpen(true);
                } else {
                  setBookOpen(true);
                }
              }}
            >
              Бронь
            </Button>
          </Tooltip>
          <Tooltip title="Новая путёвка на выделенный в шахматки номер">
            <Button
              disabled={!canAdd || !singleRoomId || !selectedFree}
              onClick={() => setSellOpen(true)}
            >
              Продать
            </Button>
          </Tooltip>
          <button
            type="button"
            className="bron-calendar__icon-btn"
            disabled={loading}
            title="Обновить"
            aria-label="Обновить"
            onClick={load}
          >
            <span className="bron-calendar__icon-btn-icon">
              <img src={refreshSrc} alt="" />
            </span>
          </button>
        </div>
      </div>
      {selection ? (
        <span className="bron-calendar__sel-info">
          Выбрано {selectedDaysCount} дн., {selectedRoomsCount} ном.
          {selectedRoomsCount > 1 ? " · можно создать отдельные путёвки" : ""}
        </span>
      ) : (
        <span className="bron-calendar__sel-info bron-calendar__sel-info--muted">
          Протащите мышью по ячейкам
        </span>
      )}

      {rebook ? (
        <Alert
          type="info"
          showIcon
          className="bron-calendar__rebook"
          message={<>
            Перебронь путёвки №{rebook.idDoc}{rebook.guestName ? ` (${rebook.guestName})` : ""}: выделите номер и период, затем нажмите «Бронь».
            Текущее размещение будет заменено.
          </>}
          action={<Button size="small" onClick={() => setRebook(null)}>Отмена</Button>}
        />
      ) : null}

      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 8 }} /> : null}

      <div className="bron-calendar__grid-wrap">
        {loading && !grid ? (
          <div className="bron-calendar__center">
            <Spin />
          </div>
        ) : (
          <table className="bron-calendar__table" style={{ "--bron-days": String(dates.length || 1) }}>
            <thead>
              <tr>
                <th className="bron-calendar__corner">Номер</th>
                {dates.map((d) => (
                  <th key={d.format("YYYY-MM-DD")} className="bron-calendar__day">
                    <div>{d.format("DD.MM")}</div>
                    <div className="bron-calendar__dow">{d.format("dd")}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {numbers.map((n, row) => {
                const idNumber = pick(n, "idNumber", "IdNumber");
                const name = pick(n, "name", "Name");
                const cat = pick(n, "catName", "CatName");
                const roomUpper = pick(n, "idUpper", "IdUpper") || 0;
                const pathLabel = pathFromSelectedToRoom(name, roomUpper, idUpper, numberById);
                const roomBookings = bookingsByRoom.get(idNumber) || { items: [], lanes: 1 };
                return (
                  <tr key={idNumber}>
                    <td className="bron-calendar__number">
                      <Tooltip
                        mouseEnterDelay={0.15}
                        title={
                          <HintLines
                            lines={[
                              name,
                              cat,
                              pathLabel && pathLabel !== name ? pathLabel : null
                            ]}
                          />
                        }
                      >
                        <div className="bron-calendar__number-hint">
                          <span className="bron-calendar__number-line">
                            <span className="bron-calendar__number-name">{name}</span>
                            {cat ? (
                              <span className="bron-calendar__number-cat">{cat}</span>
                            ) : null}
                          </span>
                          {pathLabel ? (
                            <span className="bron-calendar__number-group">{pathLabel}</span>
                          ) : null}
                        </div>
                      </Tooltip>
                    </td>
                    <td className="bron-calendar__days" colSpan={dates.length || 1}>
                      <div
                        className="bron-calendar__days-inner"
                        style={{
                          "--bron-lanes": String(roomBookings.lanes),
                          "--bron-days": String(dates.length || 1)
                        }}
                      >
                        <div className="bron-calendar__cells">
                          {dates.map((d) => {
                            const key = d.format("YYYY-MM-DD");
                            const cell = cellMap.get(`${idNumber}|${key}`);
                            const state = pick(cell, "state", "State") ?? 3;
                            const vid = pick(cell, "vidRezerv", "VidRezerv");
                            const guest = pick(cell, "guestName", "GuestName");
                            const freeAll = pick(cell, "freeAll", "FreeAll");
                            const hasDoc = Boolean(pick(cell, "idDoc", "IdDoc"));
                            const selected = isCellSelected(row, key);
                            let label = "";
                            if (!hasDoc && state !== 3 && freeAll != null) label = String(freeAll);
                            if (!hasDoc && vid === 1) label = `→${label}`;
                            else if (!hasDoc && vid === -1) label = `←${label}`;
                            return (
                              <div
                                key={key}
                                data-row={row}
                                data-date={key}
                                className={`bron-cell ${STATE_CLASS[state] || ""} ${
                                  selected ? "bron-cell--selected" : ""
                                }`}
                                title={guest || (state !== 3 ? `Свободно: ${freeAll ?? 0}` : undefined)}
                                onPointerDown={(e) => onCellPointerDown(e, row, key)}
                              >
                                {label}
                              </div>
                            );
                          })}
                        </div>
                        <div className="bron-calendar__bookings">
                          {roomBookings.items.map((b) => {
                            const guest = b.guestName || (b.idDoc ? `Путёвка ${b.idDoc}` : "");
                            const canRebook = canEdit && b.idDoc && b.idStatus >= 0 && b.idStatus < 2;
                            const symaText = formatMoney(b.syma);
                            const when = formatBookingWhen(b.dateBeg, b.dateEnd);
                            const hint = (
                              <HintLines
                                lines={[
                                  guest,
                                  b.statusText,
                                  symaText ? (b.hasDebt ? `${symaText} (долг)` : symaText) : null,
                                  when,
                                  b.guestName && b.idDoc ? `Путёвка ${b.idDoc}` : null,
                                  canRebook ? "ПКМ — действия, перетаскивание — перенос" : null
                                ]}
                              />
                            );
                            const menu = {
                              items: [
                                { key: "open", label: "Открыть путёвку" },
                                ...(canRebook ? [
                                  { type: "divider" },
                                  { key: "rebook", label: "Перебронировать" },
                                  { key: "clear", label: "Очистить размещение", danger: true }
                                ] : [])
                              ],
                              // контекстное меню — правой кнопкой; левый клик открывает карточку
                              onClick: ({ key }) => {
                                if (key === "open") setViewDocId(b.idDoc);
                                else if (key === "rebook") startRebook(b);
                                else if (key === "clear") clearPlacement(b);
                              }
                            };
                            const menuKey = `${b.idDoc}-${b.start}`;
                            return (
                              <Dropdown key={menuKey} menu={menu} trigger={["contextMenu"]} disabled={!canEdit}
                                open={menuOpenKey === menuKey}
                                onOpenChange={(opened) => {
                                  if (opened && suppressMenuRef.current) return;
                                  setMenuOpenKey(opened ? menuKey : null);
                                }}>
                              <Tooltip mouseEnterDelay={0.15} title={hint}>
                                <button
                                  type="button"
                                  className={`bron-booking bron-booking--${b.variant}${
                                    b.roundStart ? " bron-booking--round-start" : ""
                                  }${b.roundEnd ? " bron-booking--round-end" : ""}${
                                    canRebook ? " bron-booking--draggable" : ""
                                  }${dragPreview?.idDoc === b.idDoc ? " bron-booking--source" : ""}`}
                                  style={{
                                    "--start": String(b.start),
                                    "--span": String(b.span),
                                    "--lane": String(b.lane)
                                  }}
                                  onPointerDown={(e) => (canRebook ? onBookingPointerDown(e, b, row, "move") : e.stopPropagation())}
                                  onClick={() => {
                                    if (suppressMenuRef.current || !b.idDoc) return;
                                    setViewDocId(b.idDoc);
                                  }}
                                >
                                {canRebook && b.roundStart ? (
                                  <span className="bron-booking__handle bron-booking__handle--start"
                                    onPointerDown={(e) => onBookingPointerDown(e, b, row, "resize-start")} />
                                ) : null}
                                {canRebook && b.roundEnd ? (
                                  <span className="bron-booking__handle bron-booking__handle--end"
                                    onPointerDown={(e) => onBookingPointerDown(e, b, row, "resize-end")} />
                                ) : null}
                                <span className="bron-booking__name">{guest}</span>
                                {symaText ? (
                                  <span
                                    className={`bron-booking__sum${
                                      b.hasDebt ? " bron-booking__sum--debt" : ""
                                    }`}
                                  >
                                    {symaText}
                                  </span>
                                ) : null}
                                </button>
                              </Tooltip>
                              </Dropdown>
                            );
                          })}
                          {dragPreview && dragPreview.row === row ? (() => {
                            const keys = dates.map((d) => d.format("YYYY-MM-DD"));
                            const i0 = keys.indexOf(dragPreview.dateBeg);
                            const i1 = keys.indexOf(dragPreview.dateEnd);
                            const start = i0 >= 0 ? i0 + 0.5 : 0;
                            const end = i1 >= 0 ? i1 + 0.5 : keys.length;
                            const freeOk = !dates.some((d, i) => {
                              if (i < Math.floor(start) || i > Math.floor(end)) return false;
                              const cell = cellMap.get(`${idNumber}|${d.format("YYYY-MM-DD")}`);
                              const st = pick(cell, "state", "State");
                              const docHere = pick(cell, "idDoc", "IdDoc");
                              return st === 3 || (st === 2 && docHere !== dragPreview.idDoc);
                            });
                            return (
                              <div className={`bron-booking bron-booking--ghost bron-booking--${dragPreview.variant}${freeOk ? "" : " bron-booking--ghost-busy"}`}
                                style={{ "--start": String(start), "--span": String(Math.max(end - start, MIN_BAR_DAYS)), "--lane": "0" }}>
                                <span className="bron-booking__name">
                                  {dayjs(dragPreview.dateBeg).format("DD.MM")}–{dayjs(dragPreview.dateEnd).format("DD.MM")}
                                </span>
                              </div>
                            );
                          })() : null}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="bron-calendar__legend">
        <span className="bron-calendar__legend-title">Легенда статусов:</span>
        {BOOKING_KINDS.map((kind) => (
          <span key={kind.variant} className="bron-calendar__legend-item">
            <span className={`bron-calendar__legend-swatch bron-calendar__legend-swatch--${kind.variant}`} />
            {kind.label}
          </span>
        ))}
      </div>

      <DocExpBookModal
        open={bookOpen}
        idDoc={activeDocId}
        initial={{
          idNumber: singleRoomId,
          dateBeg: selection?.dateBeg,
          dateEnd: selection?.dateEnd
        }}
        onCancel={() => setBookOpen(false)}
        onDone={() => {
          setBookOpen(false);
          message.success(rebook ? "Путёвка перебронирована" : "Готово");
          const id = activeDocId;
          setRebook(null);
          load();
          if (id) setViewDocId(id);
        }}
      />

      <DocExpSellBookModal
        open={sellOpen}
        mode="room"
        asDrawer
        preset={{
          idNumber: singleRoomId,
          dateBeg: selection?.dateBeg,
          dateEnd: selection?.dateEnd
        }}
        onCancel={() => setSellOpen(false)}
        onDone={(saved) => {
          setSellOpen(false);
          load();
          const id = pick(saved, "idDoc", "IdDoc");
          if (id) setViewDocId(id);
        }}
      />

      {groupPreset && <GroupBookingDrawer open={groupOpen} rooms={groupPreset.rooms}
        period={groupPreset} onClose={() => setGroupOpen(false)} onCreated={load}
        onOpenDoc={(id) => { setGroupOpen(false); setViewDocId(id); }} />}

      <DocExp
        open={viewDocId != null}
        idDoc={viewDocId}
        canEdit={canEdit}
        onClose={() => setViewDocId(null)}
        onSaved={() => load()}
      />
    </div>
  );
};

export default BronCalendar;
